import { useEffect, useState } from 'react';
import {
  Box,
  Chip,
  CircularProgress,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import api from '../../api/axios';
import { decodeHtmlEntities } from '../../utils/pressReleaseText';

interface PressReleaseSummary {
  id: string;
  case_name: string;
  case_number: string | null;
  fraud_type: string;
  healthcare_subtype: string | null;
  settlement_usd: string | null;
  alleged_loss_usd: string | null;
  is_qui_tam: boolean;
  earliest_release_date: string | null;
  final_posture: string | null;
  primary_victim_agency: string | null;
  state_code: string | null;
  defendant_count: number;
}

interface Props {
  selectedId?: string;
  onSelect: (id: string) => void;
}

const PAGE_SIZE = 50;

const PressReleaseList = ({ selectedId, onSelect }: Props) => {
  const [items, setItems] = useState<PressReleaseSummary[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  // Debounced search — wait 300ms before firing
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const handle = setTimeout(() => {
      const params: Record<string, string> = {
        page_size: String(PAGE_SIZE),
        sort: '-settlement_usd',
      };
      if (search.trim()) params.search = search.trim();
      api
        .get('/schemes/explore/press-releases/', { params })
        .then((res) => {
          if (cancelled) return;
          setItems(res.data.results || []);
          setTotal(res.data.count || 0);
        })
        .catch((err) => {
          if (!cancelled) setError(err?.response?.data?.error || err.message || 'failed');
        })
        .finally(() => { if (!cancelled) setLoading(false); });
    }, 300);
    return () => { cancelled = true; clearTimeout(handle); };
  }, [search]);

  return (
    <Box>
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', backgroundColor: 'background.paper' }}>
        <Typography variant="overline" color="text.secondary">Press Releases</Typography>
        <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
          {loading ? 'loading…' : `${items.length} of ${total.toLocaleString()}`}
        </Typography>
        <Box sx={{ mt: 1 }}>
          <TextField
            size="small"
            fullWidth
            placeholder="Case name or defendant…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
        </Box>
      </Box>

      {loading && <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress size={24} /></Box>}
      {error && <Box sx={{ p: 2 }}><Typography color="error" variant="body2">{error}</Typography></Box>}
      {!loading && !error && items.length === 0 && (
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">No press releases match.</Typography>
        </Box>
      )}
      {items.map((pr) => {
        const active = pr.id === selectedId;
        const settlement = pr.settlement_usd ? parseFloat(pr.settlement_usd) : null;
        return (
          <Box
            key={pr.id}
            onClick={() => onSelect(pr.id)}
            sx={{
              px: 2, py: 1.5,
              borderBottom: 1, borderColor: 'divider',
              cursor: 'pointer',
              backgroundColor: active ? 'rgba(58, 68, 82, 0.08)' : 'transparent',
              borderLeft: active ? '3px solid' : '3px solid transparent',
              borderLeftColor: active ? 'primary.main' : 'transparent',
              '&:hover': { backgroundColor: active ? 'rgba(58, 68, 82, 0.12)' : 'rgba(0,0,0,0.03)' },
              transition: 'background-color 0.12s',
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
              {decodeHtmlEntities(pr.case_name) || '(unnamed case)'}
            </Typography>
            <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
              {pr.fraud_type && (
                <Chip label={pr.fraud_type} size="small" variant="outlined" sx={{ height: 18, fontSize: '0.6rem' }} />
              )}
              {pr.healthcare_subtype && (
                <Chip label={pr.healthcare_subtype} size="small" variant="outlined" sx={{ height: 18, fontSize: '0.6rem' }} />
              )}
              {pr.state_code && (
                <Chip label={pr.state_code} size="small" variant="outlined" sx={{ height: 18, fontSize: '0.6rem' }} />
              )}
              {pr.is_qui_tam && (
                <Chip label="qui tam" size="small" sx={{ height: 18, fontSize: '0.6rem', backgroundColor: 'rgba(58, 68, 82, 0.12)' }} />
              )}
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
              <Typography variant="caption" color="text.secondary">
                {pr.defendant_count} defendant{pr.defendant_count === 1 ? '' : 's'}
                {pr.earliest_release_date ? ` · ${new Date(pr.earliest_release_date).getFullYear()}` : ''}
              </Typography>
              {settlement !== null && (
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  ${settlement.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                </Typography>
              )}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

export default PressReleaseList;
